import type { WorkbookDefinition } from '@erp/shared-types';
import type { WorkbookAgentOperation } from './workbookAgentEdits';

type Ids = readonly { id: string }[];

function itemIds(definition: WorkbookDefinition): ReadonlySet<string> {
  const lists: readonly Ids[] = [
    definition.files,
    definition.sources,
    definition.sheets,
    definition.outputs,
  ];
  return new Set(lists.flatMap((list) => list.map((item) => item.id)));
}

function touchedTab(
  operation: WorkbookAgentOperation,
  ids: ReadonlySet<string>,
): string | null {
  if (operation.op === 'set_workbook' || operation.op === 'remove') return null;
  if (operation.collection === 'processSteps') return 'process';
  const id = operation.op === 'update' ? operation.id : operation.item.id;
  return typeof id === 'string' && ids.has(id) ? id : null;
}

/**
 * The tab to show once an agent edit lands: the last source, worksheet or
 * output the operations upserted or updated (an instruction shows the Process
 * tab), or null when they touched none that still exists in `definition`.
 */
export function followTargetOfOperations(
  operations: readonly WorkbookAgentOperation[],
  definition: WorkbookDefinition,
): string | null {
  const ids = itemIds(definition);
  let target: string | null = null;
  for (const operation of operations)
    target = touchedTab(operation, ids) ?? target;
  return target;
}

/**
 * As `followTargetOfOperations`, for a whole-definition replacement: the last
 * source, worksheet or output that is new or differs from `before`.
 */
export function followTargetOfDefinition(
  before: WorkbookDefinition,
  after: WorkbookDefinition,
): string | null {
  const lists = ['files', 'sources', 'sheets', 'outputs'] as const;
  let target: string | null = null;
  for (const key of lists) {
    const previous = new Map<string, string>(
      before[key].map((item) => [item.id, JSON.stringify(item)]),
    );
    for (const item of after[key])
      if (previous.get(item.id) !== JSON.stringify(item)) target = item.id;
  }
  return target;
}

/** The ids of the files, sources, worksheets and outputs `before` had that `after` lacks. */
export function removedTabIds(
  before: WorkbookDefinition,
  after: WorkbookDefinition,
): readonly string[] {
  const kept = itemIds(after);
  return [...itemIds(before)].filter((id) => !kept.has(id));
}
